/**
 * PublishModal — a deliberate, two-step confirmation before a story goes
 * live: review, then a short delightful success state.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Button, MascotContainer, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import StoryCover from './StoryCover';
import { VISIBILITY_META } from './constants';
import { getStoryWordCount } from './utils/storyHelpers';
import beeFly from '@/assets/bee-fly.png';

function ReviewStep({ story, visibility, setVisibility, onCancel, onConfirm }) {
  const wordCount = getStoryWordCount(story);

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">Ready to share your story?</h2>
      <p className="mt-1 text-sm text-subtle">Take a last look before it goes out into the world.</p>

      <div className="mt-4 flex gap-3 rounded-2xl border border-line bg-cream/60 p-3">
        <StoryCover story={story} className="h-24 w-18 shrink-0" />
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-semibold text-ink">{story.title}</h3>
          <p className="mt-0.5 line-clamp-2 text-xs text-subtle">{story.description || 'No description yet.'}</p>
          <p className="mt-1.5 text-[11px] text-subtle">{story.genre} · {story.chapters.length} chapters · {wordCount.toLocaleString()} words</p>
          {story.tags?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {story.tags.map((t) => <span key={t} className="rounded-full bg-cream px-2 py-0.5 text-[10px] text-subtle">{t}</span>)}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Who can see this?</label>
        <div className="mt-1.5 flex flex-col gap-2">
          {['unlisted', 'published'].map((key) => {
            const meta = VISIBILITY_META[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setVisibility(key)}
                className={cx(
                  'rounded-xl border px-3 py-2 text-left transition-colors',
                  visibility === key ? 'border-coral bg-coral/10' : 'border-line hover:border-coral/40'
                )}
              >
                <p className="text-sm font-semibold text-ink">{meta.label}</p>
                <p className="text-[11px] text-subtle">{meta.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="secondary" icon={false} onClick={onCancel}>Keep as Draft</Button>
        <Button variant="primary" icon={false} onClick={onConfirm} data-testid="my-writing-publish-confirm">Publish Story</Button>
      </div>
    </div>
  );
}

function SuccessStep({ story, onViewStory, onBackToDashboard }) {
  const handleShare = () => {
    const url = `${window.location.origin}/app/studio/${story.id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    toast('Link copied to clipboard');
  };

  return (
    <div className="text-center">
      <MascotContainer src={beeFly} alt="" className="mx-auto h-24 w-24" />
      <h2 className="mt-3 font-display text-xl font-semibold text-ink">Your story is out in the world. 🐝</h2>
      <p className="mt-1.5 text-sm text-subtle">Time to let some readers discover it.</p>

      <div className="mt-6 flex flex-col gap-2.5">
        <Button variant="primary" icon={false} onClick={onViewStory}>View Story</Button>
        <Button variant="secondary" icon={false} onClick={handleShare}>Share Story</Button>
        <button type="button" onClick={onBackToDashboard} data-testid="my-writing-publish-done" className="text-xs font-medium text-subtle hover:text-coral">Back to Studio</button>
      </div>
    </div>
  );
}

function ModalBody({ story, onClose, onPublish, onViewStory, onBackToDashboard }) {
  const [step, setStep] = useState('review');
  const [visibility, setVisibility] = useState(story.visibility === 'private' ? 'unlisted' : story.visibility);

  const handleConfirm = () => {
    onPublish(visibility);
    setStep('success');
  };

  if (step === 'success') {
    return <SuccessStep story={story} onViewStory={onViewStory} onBackToDashboard={onBackToDashboard} />;
  }

  return (
    <ReviewStep
      story={story}
      visibility={visibility}
      setVisibility={setVisibility}
      onCancel={onClose}
      onConfirm={handleConfirm}
    />
  );
}

export default function PublishModal({ story, isOpen, onClose, onPublish }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const navigate = useNavigate();
  const onViewStory = () => { onClose(); navigate(`/app/studio/${story.id}?preview=1`); };
  const onBackToDashboard = () => { onClose(); navigate('/app/studio'); };

  if (!story) return null;

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="w-[92vw] max-w-md rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Publish story</DialogTitle>
          <ModalBody story={story} onClose={onClose} onPublish={onPublish} onViewStory={onViewStory} onBackToDashboard={onBackToDashboard} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Publish story</DrawerTitle>
        <ModalBody story={story} onClose={onClose} onPublish={onPublish} onViewStory={onViewStory} onBackToDashboard={onBackToDashboard} />
      </DrawerContent>
    </Drawer>
  );
}
